// @givmo/app-admin — Steps Competition DETAIL / manage (EP-WEB10).
//
// The one screen an admin runs a competition from: `GET …/{id}` (admin detail, which uniquely carries
// the company-wallet money context) plus the lifecycle actions — edit (DRAFT only), publish (places the
// prize hold), cancel (releases it), retry payout (PAYOUT_PENDING only) — and the entrants panel.
//
// ★ THE HELD-PRIZE DISPLAY LAW. Two different money figures live on this screen and conflating them is
// the exact bug the shipped iOS admin surface was fixed for (SC-IOS4):
//   - "Held for this prize" is THIS competition's own immutable `prize_amount`, shown ONLY while the
//     hold is live (PUBLISHED / ACTIVE / GRACE / PAYOUT_PENDING). FINALIZED says it was paid; CANCELED
//     says it was released; DRAFT says what publishing WILL hold.
//   - The wallet figures are ALWAYS labelled as the wallet's — "Company wallet available" and "Held
//     across all competitions" (the Σ of every active hold, which is a different number).
// `prizeHoldView()` owns that law; this screen only renders what it returns. Never gate on
// `prize_hold_id` — it persists as a denorm after the hold is consumed or released.
//
// Cancel is confirm-gated (it releases held money and ends the competition) and idempotent server-side.
// Nothing here promises a winner: the degenerate-competition rule is stated wherever the prize is.
//
// DARK: reached only via the `/competitions/*` routes, which redirect while
// `VITE_COMPANY_STEPS_COMPETITIONS_ENABLED` is off; this page also bounces (defense in depth).

import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  stepsCompetitionsAdminCancelAdminStepsCompetitionMutation,
  stepsCompetitionsAdminGetAdminStepsCompetitionOptions,
  stepsCompetitionsAdminGetAdminStepsCompetitionQueryKey,
  stepsCompetitionsAdminRetryPayoutAdminStepsCompetitionMutation,
} from '@givmo/api-client/query';
import type { AdminStepsCompetitionOut } from '@givmo/api-client';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Icon,
  PageHeader,
  Skeleton,
  testIdProp,
  toast,
} from '@givmo/design-system';
import { ArrowLeft } from 'lucide-react';
import { ConfirmDialog } from '../../components/confirm-dialog';
import { isCompanyStepsCompetitionsEnabled } from '../../lib/env';
import { useEmployerModulePaths } from '../../lib/module-paths';
import {
  DEGENERATE_RULE_COPY,
  canCancel,
  canPublish,
  canRetryPayout,
  competitionStatusView,
  errorMessage,
  formatDay,
  formatPrize,
  formatTimestampDay,
  hasEntrantSurface,
  isDraft,
  prizeHoldView,
  visibilityLabel,
  walletAvailable,
  walletHeldAggregate,
} from './competitions-decode';
import { CompetitionEntriesPanel } from './competition-entries-panel';
import { PublishCompetitionDialog } from './publish-competition-dialog';

const PREFIX = 'competitions-detail';

export interface CompetitionDetailPageProps {
  competitionId: string;
}

export function CompetitionDetailPage({ competitionId }: CompetitionDetailPageProps) {
  const navigate = useNavigate();
  const paths = useEmployerModulePaths();
  const enabled = isCompanyStepsCompetitionsEnabled();

  useEffect(() => {
    if (!enabled) void navigate({ to: paths.dashboard });
  }, [enabled, navigate, paths.dashboard]);
  if (!enabled) return null;

  return <CompetitionDetail competitionId={competitionId} />;
}

function DetailShell({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const paths = useEmployerModulePaths();
  return (
    <div
      className="mx-auto flex w-full max-w-[1100px] flex-col gap-24 px-24 py-24"
      {...testIdProp('page-competitions-detail')}
    >
      <PageHeader
        title="Competition"
        actions={
          <Button
            appearance="saas"
            tone="secondary"
            leadingIcon={<Icon icon={ArrowLeft} size={16} aria-hidden />}
            onClick={() => void navigate({ to: paths.competitions })}
            testId={`${PREFIX}-back`}
          >
            Back
          </Button>
        }
      />
      {children}
    </div>
  );
}

function CompetitionDetail({ competitionId }: { competitionId: string }) {
  const navigate = useNavigate();
  const paths = useEmployerModulePaths();
  const queryClient = useQueryClient();
  const [publishOpen, setPublishOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);

  const detailKey = stepsCompetitionsAdminGetAdminStepsCompetitionQueryKey({
    path: { steps_competition_id: competitionId },
  });
  const detail = useQuery(
    stepsCompetitionsAdminGetAdminStepsCompetitionOptions({
      path: { steps_competition_id: competitionId },
    }),
  );

  const invalidateReads = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: detailKey }),
      queryClient.invalidateQueries({
        queryKey: ['steps_competitions_admin-list_admin_steps_competitions'],
      }),
    ]);
  };

  const cancel = useMutation({
    ...stepsCompetitionsAdminCancelAdminStepsCompetitionMutation(),
    onSuccess: async () => {
      setCancelOpen(false);
      await invalidateReads();
      toast.success('Competition canceled. The prize hold was released.');
    },
    onError: (error) => {
      toast.error(errorMessage(error, 'We couldn’t cancel this competition. Please try again.'));
    },
  });

  const retryPayout = useMutation({
    ...stepsCompetitionsAdminRetryPayoutAdminStepsCompetitionMutation(),
    onSuccess: async (result: AdminStepsCompetitionOut) => {
      await invalidateReads();
      // The backend re-enters the SAME fenced finalize flow: it either FINALIZEs or stays
      // PAYOUT_PENDING (the winner's charity is still unavailable). Report which actually happened
      // rather than claiming success either way.
      if (result.status === 'FINALIZED') {
        toast.success('Payout completed. The prize was paid to the winner’s charity.');
      } else {
        toast.error('The payout could not complete yet. We’ll keep retrying — try again later.');
      }
    },
    onError: (error) => {
      toast.error(errorMessage(error, 'We couldn’t retry the payout. Please try again.'));
    },
  });

  if (detail.isPending) {
    return (
      <DetailShell>
        <div className="flex flex-col gap-16" {...testIdProp(`${PREFIX}-loading`)}>
          <Skeleton className="h-[160px] w-full rounded-lg" />
          <Skeleton className="h-[220px] w-full rounded-lg" />
        </div>
      </DetailShell>
    );
  }

  if (detail.isError) {
    return (
      <DetailShell>
        <Card {...testIdProp(`${PREFIX}-error`)}>
          <CardBody className="flex flex-col items-center gap-8 py-24 text-center">
            <p className="text-s14 font-w500 text-danger-700">
              {errorMessage(detail.error, 'Failed to load this competition.')}
            </p>
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              onClick={() => void detail.refetch()}
              testId={`${PREFIX}-retry`}
            >
              Try again
            </Button>
          </CardBody>
        </Card>
      </DetailShell>
    );
  }

  const competition = detail.data as AdminStepsCompetitionOut;
  const status = competitionStatusView(competition.status);
  const hold = prizeHoldView(competition);
  const available = walletAvailable(competition);
  const walletHeld = walletHeldAggregate(competition);

  return (
    <div
      className="mx-auto flex w-full max-w-[1100px] flex-col gap-24 px-24 py-24"
      {...testIdProp('page-competitions-detail')}
    >
      <PageHeader
        title={competition.name}
        subtitle={`${formatDay(competition.start_date)} – ${formatDay(competition.end_date)} · ${visibilityLabel(
          competition.visibility,
        )}`}
        actions={
          <div className="flex flex-wrap items-center gap-8">
            <Button
              appearance="saas"
              tone="secondary"
              leadingIcon={<Icon icon={ArrowLeft} size={16} aria-hidden />}
              onClick={() => void navigate({ to: paths.competitions })}
              testId={`${PREFIX}-back`}
            >
              Back
            </Button>
            {isDraft(competition) && (
              <Button
                appearance="saas"
                tone="secondary"
                onClick={() =>
                  void navigate({
                    to: paths.competitionEdit,
                    params: { competitionId: competition.id },
                  })
                }
                testId={`${PREFIX}-edit`}
              >
                Edit
              </Button>
            )}
            {canCancel(competition) && (
              <Button
                appearance="saas"
                tone="danger"
                onClick={() => setCancelOpen(true)}
                testId={`${PREFIX}-cancel`}
              >
                Cancel
              </Button>
            )}
            {canRetryPayout(competition) && (
              <Button
                appearance="saas"
                tone="secondary"
                loading={retryPayout.isPending}
                onClick={() =>
                  retryPayout.mutate({ path: { steps_competition_id: competition.id } })
                }
                testId={`${PREFIX}-retry-payout`}
              >
                Retry payout
              </Button>
            )}
            {canPublish(competition) && (
              <Button
                appearance="saas"
                tone="primary"
                onClick={() => setPublishOpen(true)}
                testId={`${PREFIX}-publish`}
              >
                Publish
              </Button>
            )}
          </div>
        }
      />

      <div className="flex flex-wrap items-center gap-8">
        <Badge tone={status.tone} dot testId={`${PREFIX}-status`}>
          {status.label}
        </Badge>
        <span className="font-sans text-s13 text-text-tertiary">
          {competition.participant_count ?? 0}{' '}
          {(competition.participant_count ?? 0) === 1 ? 'entrant' : 'entrants'}
        </span>
        {competition.published_at != null && (
          <span className="font-sans text-s13 text-text-tertiary">
            Published {formatTimestampDay(competition.published_at)}
          </span>
        )}
      </div>

      {competition.description !== null &&
        competition.description !== undefined &&
        competition.description.trim() !== '' && (
          <p className="font-sans text-s14 text-text-secondary" {...testIdProp(`${PREFIX}-about`)}>
            {competition.description}
          </p>
        )}

      {/* ── Prize + money ─────────────────────────────────────────────────────────────────────── */}
      <Card {...testIdProp(`${PREFIX}-prize-card`)}>
        <CardHeader title="Prize" />
        <CardBody className="flex flex-col gap-12">
          <div className="flex items-baseline justify-between">
            <span className="font-sans text-s14 text-text-secondary">
              Prize (donated to the winner’s charity)
            </span>
            <span
              className="font-sans text-s22 font-w700 tabular-nums text-text-strong"
              {...testIdProp(`${PREFIX}-prize`)}
            >
              {formatPrize(competition)}
            </span>
          </div>

          {/* The per-competition hold state — status-driven, never the wallet aggregate. */}
          {hold.kind === 'held' && (
            <div className="flex items-center justify-between">
              <span className="font-sans text-s14 text-text-secondary">{hold.label}</span>
              <span
                className="font-sans text-s15 font-w600 tabular-nums text-text-strong"
                {...testIdProp(`${PREFIX}-held`)}
              >
                {hold.amount}
              </span>
            </div>
          )}
          {hold.kind !== 'none' && (
            <p
              className="font-sans text-s12 text-text-tertiary"
              {...testIdProp(`${PREFIX}-hold-note`)}
            >
              {hold.note}
            </p>
          )}

          <p className="font-sans text-s12 text-text-tertiary">{DEGENERATE_RULE_COPY}</p>

          {/* Wallet-level context — ALWAYS labelled as the wallet's, never as this competition's hold. */}
          {(available !== null || walletHeld !== null) && (
            <div className="mt-4 flex flex-col gap-8 border-t border-border-subtle pt-12">
              {available !== null && (
                <div className="flex items-center justify-between">
                  <span className="font-sans text-s14 text-text-secondary">
                    Company wallet available
                  </span>
                  <span
                    className="font-sans text-s15 font-w600 tabular-nums text-text-strong"
                    {...testIdProp(`${PREFIX}-wallet-available`)}
                  >
                    {available}
                  </span>
                </div>
              )}
              {walletHeld !== null && (
                <div className="flex items-center justify-between">
                  <span className="font-sans text-s14 text-text-secondary">
                    Held across all competitions
                  </span>
                  <span
                    className="font-sans text-s15 font-w600 tabular-nums text-text-strong"
                    {...testIdProp(`${PREFIX}-wallet-held`)}
                  >
                    {walletHeld}
                  </span>
                </div>
              )}
            </div>
          )}
        </CardBody>
      </Card>

      {hasEntrantSurface(competition) && (
        <CompetitionEntriesPanel
          competitionId={competition.id}
          actionable={
            competition.status !== 'FINALIZED' &&
            competition.status !== 'CANCELED' &&
            competition.status !== 'DRAFT'
          }
        />
      )}

      <PublishCompetitionDialog
        open={publishOpen}
        onOpenChange={setPublishOpen}
        competition={competition}
      />

      <ConfirmDialog
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        title="Cancel this competition?"
        subject={competition.name}
        body={
          isDraft(competition)
            ? 'This draft will be canceled. No money has been held, so nothing is released.'
            : 'The competition ends now, no prize is awarded, and the held amount is released back to your available balance.'
        }
        confirmLabel="Cancel competition"
        destructive
        loading={cancel.isPending}
        onConfirm={() => cancel.mutate({ path: { steps_competition_id: competition.id } })}
        testId={`${PREFIX}-cancel-confirm`}
      />
    </div>
  );
}
